import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { connect } from 'pwa-helpers/connect-mixin.js';
import { store } from '../../store/store.js';
import { load } from '../../store/composition/composition.js';
import './sdb-composition-status.js';

/**
 * Shows the composition status of a given user (idString), read-only.
 *
 * Intended for admins: the state is loaded into `state.composition.list[idString]`,
 * the same slot the admin reset button uses, so a reset refreshes this view as well.
 */
@customElement('sdb-composition-user-state')
class SdbCompositionUserState extends connect(store)(LitElement) {

    @property({ type: String })
    accessor compositionId;

    @property({ type: String })
    accessor idString;

    @property({ type: Boolean })
    accessor light = false;

    @property({ type: Boolean })
    accessor small = false;

    @property({ type: Boolean })
    accessor compressed = false;

    @property({ type: String })
    accessor noSettings = 'showStatus'; // showAll, showStatus, hideAll

    // Status as rows (label left, value right) instead of the summary columns.
    @property({ type: Boolean, attribute: 'rows-layout' })
    accessor rowsLayout = false;

    @state()
    accessor composition;

    @state()
    accessor isLoading = false;

    // A load for the current compositionId/idString has completed (or failed for real).
    _loaded = false;

    // A load dispatched by this component is still in flight.
    _pending = false;

    stateChanged(state) {
        this.composition = this.idString ? state.composition.list?.[this.idString] : undefined;
        this.isLoading = state.composition.status === 'loading';

        // The load thunk refuses to run while another load is in progress, so a load that was
        // skipped is retried here as soon as the store becomes idle again.
        this._ensureLoaded();
    }

    updated(changedProperties) {
        if (changedProperties.has('compositionId') || changedProperties.has('idString')) {
            this._loaded = false;
            this._ensureLoaded();
        }
    }

    render() {
        if (!this.composition) {
            return nothing;
        }

        return html`
            <sdb-composition-status
                .composition=${this.composition}
                noSettings=${this.noSettings}
                ?light=${this.light}
                ?small=${this.small}
                ?compressed=${this.compressed}
                ?rows-layout=${this.rowsLayout}
                readOnly>
            </sdb-composition-status>
        `;
    }

    async _ensureLoaded() {
        if (this._loaded || this._pending || !this.compositionId || !this.idString) {
            return;
        }

        if (store.getState().composition.status === 'loading') {
            return; // stateChanged calls again once the running load has finished
        }

        this._pending = true;

        const result = await store.dispatch(load({
            compositionId: this.compositionId,
            idString: this.idString,
        }));

        this._pending = false;

        if (result.meta?.condition) {
            // Skipped because another load was running. Accept the entry that load produced
            // when it was for the same user, otherwise retry once the store is idle.
            this._loaded = !!this.composition;
            return;
        }

        this._loaded = true;
    }
}
